import { db, CallFoundryDexie } from '../db';
import type {
  CanonicalTrainingEvent,
  EventType,
  IndependenceMode,
  Pillar,
  TrainingAttempt,
} from '../models';
import { getDeviceId } from '../identity/deviceIdentity';
import { getLocalLearnerId, getActiveUserId, isLocalLearner } from '../identity/learnerIdentity';
import {
  recomputeCompetencyProgress,
  recomputeLearnerProgress,
} from '../progress/progressEngine';
import { assertNoRawAudioPayload } from '../sync/audioSyncRejection';

export const COMPETENCY_METADATA: Record<string, { name: string; pillar: Pillar }> = {
  'COM-01': { name: 'Active Listening & Verification', pillar: 'communication' },
  'COM-02': { name: 'Rapid Numerical Parsing', pillar: 'communication' },
  'COM-03': { name: 'Conversational Pace & Tone', pillar: 'communication' },
  'SUP-01': { name: 'Root Cause Discovery', pillar: 'support' },
  'SUP-02': { name: 'De-escalation & Empathy', pillar: 'support' },
  'SUP-03': { name: 'Call Control & Hold Etiquette', pillar: 'support' },
  'SAL-01': { name: 'Consultative Value Framing', pillar: 'sales' },
  'SAL-02': { name: 'Objection Reframing', pillar: 'sales' },
  'SAL-03': { name: 'Seamless Closing', pillar: 'sales' },
};

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export interface RecordEventInput {
  eventType: EventType;
  skillId?: string;
  scenarioId?: string;
  difficulty?: number;
  independenceMode?: IndependenceMode;
  payload?: Record<string, unknown>;
  userId?: string; // Optional target user context; defaults to active user
  originLearnerId?: string; // Optional origin learner id; defaults to installation's localLearnerId
  // When eventType is 'ATTEMPT_COMPLETED':
  attemptData?: {
    drillId: string;
    score: number;
    timeSpentMs: number;
    userResponses?: Record<string, unknown>;
    feedback?: string;
  };
}

export class EventService {
  private dexie: CallFoundryDexie;

  constructor(databaseInstance: CallFoundryDexie = db) {
    this.dexie = databaseInstance;
  }

  /**
   * The Canonical Write Path
   * User Action -> Commit to Dexie -> Update UI/Cache -> Queue Sync
   * Executes entirely within an atomic ACID Dexie transaction.
   * Device sequence numbers are monotonically assigned inside the transaction to eliminate race conditions.
   */
  async recordTrainingEvent(input: RecordEventInput): Promise<{
    event: CanonicalTrainingEvent;
    attempt?: TrainingAttempt;
    durationMs: number;
  }> {
    const startTime = performance.now();

    const eventId = generateUUID();
    const originLearnerId = input.originLearnerId ?? getLocalLearnerId();
    const userId = input.userId ?? getActiveUserId();
    const deviceId = getDeviceId();
    const clientCreatedAt = new Date().toISOString();

    let attempt: TrainingAttempt | undefined;

    if (input.eventType === 'ATTEMPT_COMPLETED' && input.attemptData && input.skillId) {
      attempt = {
        id: generateUUID(),
        drillId: input.attemptData.drillId,
        skillId: input.skillId,
        score: input.attemptData.score,
        evidenceConfidence: 0.2, // Minimal M2 basic confidence
        independenceMode: input.independenceMode ?? 'practice',
        difficulty: input.difficulty ?? 1.0,
        timeSpentMs: input.attemptData.timeSpentMs,
        userResponses: input.attemptData.userResponses ?? {},
        feedback: input.attemptData.feedback ?? '',
        completedAt: clientCreatedAt,
        synced: false,
      };
    }

    let createdEvent!: CanonicalTrainingEvent;

    // Atomic transaction across affected tables
    await this.dexie.transaction(
      'rw',
      [
        this.dexie.events,
        this.dexie.attempts,
        this.dexie.masteryProfiles,
        this.dexie.learnerProfiles,
        this.dexie.syncQueue,
        this.dexie.syncMetadata,
        this.dexie.deviceMetadata,
      ],
      async () => {
        // 1. Atomically obtain next device sequence
        const seqRecord = await this.dexie.deviceMetadata.get('deviceSequence');
        const currentSeq = typeof seqRecord?.value === 'number' ? seqRecord.value : 0;
        const nextSeq = currentSeq + 1;
        await this.dexie.deviceMetadata.put({
          key: 'deviceSequence',
          value: nextSeq,
          updatedAt: clientCreatedAt,
        });

        // 2. Build immutable canonical event
        createdEvent = {
          eventId,
          originLearnerId,
          deviceId,
          deviceSequence: nextSeq,
          eventType: input.eventType,
          skillId: input.skillId,
          scenarioId: input.scenarioId,
          difficulty: input.difficulty ?? 1.0,
          independenceMode: input.independenceMode ?? 'practice',
          scoringVersion: '1.0.0',
          contentVersion: '1.0.0',
          payloadVersion: '1.0.0',
          clientCreatedAt,
          payload:
            input.payload ??
            (input.attemptData
              ? {
                  attemptId: attempt?.id,
                  drillId: input.attemptData.drillId,
                  score: input.attemptData.score,
                  timeSpentMs: input.attemptData.timeSpentMs,
                  userResponses: input.attemptData.userResponses ?? {},
                  feedback: input.attemptData.feedback ?? '',
                }
              : {}),
        };

        // 3. Commit canonical event (immutable)
        await this.dexie.events.put(createdEvent);

        // 4. Record transport metadata in syncMetadata
        await this.dexie.syncMetadata.put({
          eventId,
          status: 'pending',
          localAckAt: clientCreatedAt,
          accountUid: isLocalLearner(userId) ? undefined : userId,
        });

        // 5. If attempt, record attempt and update derived progress
        if (attempt && input.skillId) {
          await this.dexie.attempts.put(attempt);

          const skillMeta = COMPETENCY_METADATA[input.skillId] ?? {
            name: input.skillId,
            pillar: 'communication' as Pillar,
          };
          const skillAttempts = await this.dexie.attempts
            .where('skillId')
            .equals(input.skillId)
            .toArray();

          const competencyMastery = recomputeCompetencyProgress(
            input.skillId,
            skillMeta.name,
            skillMeta.pillar,
            skillAttempts
          );
          await this.dexie.masteryProfiles.put(competencyMastery);

          const allCompetencies = await this.dexie.masteryProfiles.toArray();
          const existingProfile = await this.dexie.learnerProfiles.get(userId);
          const updatedProfile = recomputeLearnerProgress(
            userId,
            allCompetencies,
            existingProfile?.displayName ?? 'Agent 402',
            existingProfile?.activeJobProfile ?? 'inbound_telecom_care'
          );
          await this.dexie.learnerProfiles.put(updatedProfile);
        }

        // 6. Audio privacy boundary enforcement (Directive 8)
        assertNoRawAudioPayload(createdEvent.payload);

        // 7. Enqueue for sync transport
        await this.dexie.syncQueue.put({
          id: generateUUID(),
          eventId: createdEvent.eventId,
          userId,
          accountUid: isLocalLearner(userId) ? undefined : userId,
          attempts: 0,
          lastAttemptAt: null,
          status: 'pending',
          createdAt: clientCreatedAt,
        });
      }
    );

    const durationMs = performance.now() - startTime;
    return { event: createdEvent, attempt, durationMs };
  }

  /**
   * Retrieves all canonical events for a given user or origin learner,
   * sorted deterministically by logical device sequence.
   */
  async getEventsForUser(userId: string): Promise<CanonicalTrainingEvent[]> {
    const byUser = await this.dexie.events.where('userId').equals(userId).toArray();
    if (byUser.length > 0) {
      return byUser.sort((a, b) => a.deviceSequence - b.deviceSequence);
    }
    const byOrigin = await this.dexie.events.where('originLearnerId').equals(userId).toArray();
    return byOrigin.sort((a, b) => a.deviceSequence - b.deviceSequence);
  }

  /**
   * Marks an event as successfully synchronized to remote Firestore replica
   * Updates syncMetadata without mutating the immutable CanonicalTrainingEvent.
   */
  async markEventSynced(
    eventId: string,
    serverReceivedAt: string,
    remoteDocPath?: string
  ): Promise<void> {
    await this.dexie.transaction('rw', [this.dexie.syncMetadata, this.dexie.syncQueue], async () => {
      await this.dexie.syncMetadata.update(eventId, {
        status: 'synced',
        localAckAt: new Date().toISOString(),
        serverReceivedAt,
        remoteDocPath,
      });

      const queueItem = await this.dexie.syncQueue.where('eventId').equals(eventId).first();
      if (queueItem) {
        await this.dexie.syncQueue.delete(queueItem.id);
      }
    });
  }

  /**
   * Deletion via Tombstone Event (Idempotent Deletion)
   */
  async createTombstone(
    targetType: string,
    targetId: string,
    reason = 'User deleted item'
  ): Promise<CanonicalTrainingEvent> {
    const result = await this.recordTrainingEvent({
      eventType: 'TOMBSTONE_DELETED',
      payload: {
        targetType,
        targetId,
        tombstone: true,
        reason,
        deletedAt: new Date().toISOString(),
      },
    });
    return result.event;
  }
}

export const eventService = new EventService();
